import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Eye, EyeOff, Loader2, Mail, MessageSquare, MailSearch } from 'lucide-react';
import {
  getCurrentUserInfo,
  updateSelf,
  getResumeFetchSecret,
  type User,
  type UpdateSelfData,
} from '../api/user';
import { useToast } from '../components/ui/toast';

// 开关组件（无第三方依赖的简易 Switch）
const Switch = ({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
      checked ? 'bg-blue-600' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-[18px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const ProfilePage = () => {
  const [userInfo, setUserInfo] = useState<User | null>(null);
  // 简历抓取邮箱只对 HR 开放
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { showToast } = useToast();

  const [form, setForm] = useState({
    real_name: '',
    email: '',
    phone: '',
    password: '',
  });

  // 消息通知开关（默认都关闭，由用户手动开启）
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyFeishu, setNotifyFeishu] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);

  // 简历抓取配置
  const [fetchForm, setFetchForm] = useState({
    resume_fetch_email: '',
    resume_fetch_password: '',
    resume_fetch_smtp: '',
  });
  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [fetchPasswordVisible, setFetchPasswordVisible] = useState(false);
  const [fetchSmtpVisible, setFetchSmtpVisible] = useState(false);
  const [savingFetch, setSavingFetch] = useState(false);
  // 密码/SMTP 的真实值（点击眼睛后从后端获取）
  const [revealed, setRevealed] = useState<{ password?: string; smtp?: string }>({});

  const loadUserInfo = async () => {
    try {
      setLoading(true);
      const data = await getCurrentUserInfo();
      setUserInfo(data);
      setForm({
        real_name: data.real_name || '',
        email: data.email || '',
        phone: data.phone || '',
        password: '',
      });
      setNotifyEmail(!!data.notify_email_enabled);
      setNotifyFeishu(!!data.notify_feishu_enabled);
      setFetchEnabled(!!data.resume_fetch_enabled);
      setFetchForm({
        resume_fetch_email: data.resume_fetch_email || '',
        resume_fetch_password: data.resume_fetch_password || '',
        resume_fetch_smtp: data.resume_fetch_smtp || '',
      });
      setRevealed({});
      setFetchPasswordVisible(false);
      setFetchSmtpVisible(false);
    } catch (error) {
      console.error('获取用户信息失败:', error);
      showToast('获取用户信息失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserInfo();
  }, []);

  const handleSave = () => {
    if (!form.real_name.trim()) {
      showToast('姓名不能为空', 'warning');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const updateData: UpdateSelfData = {};
      if (form.real_name !== (userInfo?.real_name || '')) {
        updateData.real_name = form.real_name;
      }
      if (form.email !== (userInfo?.email || '')) {
        updateData.email = form.email;
      }
      if (form.phone !== (userInfo?.phone || '')) {
        updateData.phone = form.phone;
      }
      if (form.password) {
        updateData.password = form.password;
      }

      if (Object.keys(updateData).length === 0) {
        showToast('没有需要更新的内容', 'warning');
        return;
      }

      await updateSelf(updateData);
      showToast('个人信息更新成功');
      setForm((prev) => ({ ...prev, password: '' }));
      loadUserInfo();
    } catch (error) {
      console.error('更新失败:', error);
      showToast('更新失败，请稍后重试', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // 切换通知开关（即时保存）
  // 开启飞书通知时后端会立即用邮箱/手机号去飞书通讯录校验，匹配不到则报错并保持关闭
  const handleToggleNotify = async (type: 'email' | 'feishu', value: boolean) => {
    const prevEmail = notifyEmail;
    const prevFeishu = notifyFeishu;

    // 前端前置校验：没有联系方式时不允许开启
    if (value) {
      if (type === 'email' && !hasEmail) {
        showToast('请先填写邮箱并保存后，再开启邮件通知', 'warning');
        return;
      }
      if (type === 'feishu' && !hasContact) {
        showToast('请先填写邮箱或电话并保存后，再开启飞书通知', 'warning');
        return;
      }
    }

    if (type === 'email') setNotifyEmail(value);
    else setNotifyFeishu(value);

    setSavingNotify(true);
    try {
      await updateSelf(
        type === 'email' ? { notify_email_enabled: value } : { notify_feishu_enabled: value }
      );
      showToast(`${type === 'email' ? '邮件' : '飞书'}通知已${value ? '开启' : '关闭'}`);
    } catch (error: any) {
      console.error('更新通知设置失败:', error);
      showToast(error?.response?.data?.detail || '更新通知设置失败', 'error');
      // 校验不通过时回到关闭状态
      setNotifyEmail(prevEmail);
      setNotifyFeishu(prevFeishu);
    } finally {
      setSavingNotify(false);
    }
  };

  // 眼睛图标：显示真实值
  const handleReveal = async (field: 'password' | 'smtp') => {
    const isPassword = field === 'password';
    const visible = isPassword ? fetchPasswordVisible : fetchSmtpVisible;
    if (visible) {
      if (isPassword) setFetchPasswordVisible(false);
      else setFetchSmtpVisible(false);
      return;
    }

    try {
      if (!revealed.password && !revealed.smtp) {
        const secret = await getResumeFetchSecret();
        setRevealed({ password: secret.resume_fetch_password, smtp: secret.resume_fetch_smtp });
      }
      if (isPassword) setFetchPasswordVisible(true);
      else setFetchSmtpVisible(true);
    } catch (error) {
      console.error('获取配置明文失败:', error);
      showToast('获取配置明文失败', 'error');
    }
  };

  // 保存简历抓取配置
  const handleSaveFetchConfig = async (nextEnabled?: boolean) => {
    const enabled = nextEnabled === undefined ? fetchEnabled : nextEnabled;
    const passwordValue = fetchPasswordVisible
      ? fetchForm.resume_fetch_password
      : fetchForm.resume_fetch_password;

    if (enabled) {
      if (!fetchForm.resume_fetch_email.trim() || !passwordValue.trim() || !fetchForm.resume_fetch_smtp.trim()) {
        showToast('开启简历抓取前请填写抓取邮箱、邮箱密码和IMAP服务器', 'warning');
        return;
      }
    }

    setSavingFetch(true);
    try {
      await updateSelf({
        resume_fetch_email: fetchForm.resume_fetch_email,
        resume_fetch_password: passwordValue,
        resume_fetch_smtp: fetchForm.resume_fetch_smtp,
        resume_fetch_enabled: enabled,
      });
      setFetchEnabled(enabled);
      showToast('简历抓取配置已保存');
      loadUserInfo();
    } catch (error: any) {
      console.error('保存简历抓取配置失败:', error);
      showToast(error?.response?.data?.detail || '保存简历抓取配置失败', 'error');
      setFetchEnabled(!!userInfo?.resume_fetch_enabled);
    } finally {
      setSavingFetch(false);
    }
  };

  const isHRUser = userInfo?.role === 'HR';
  const hasEmail = !!(userInfo?.email || '').trim();
  const hasPhone = !!(userInfo?.phone || '').trim();
  const hasContact = hasEmail || hasPhone;

  return (
    <div className="h-full flex flex-col overflow-y-auto">
      {/* 页面标题 */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-800">个人信息</h1>
      </div>

      {/* 内容卡片 */}
      <Card className="flex-1">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">加载中...</span>
            </div>
          ) : (
            <div className="space-y-8">
              {/* 基本信息（只读） */}
              <div>
                <h2 className="text-sm font-medium text-gray-500 mb-4">基本信息</h2>
                <div className="grid grid-cols-5 gap-6">
                  <div>
                    <Label className="text-xs text-gray-400">用户ID</Label>
                    <div className="mt-1.5 text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                      {userInfo?.id}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">用户名</Label>
                    <div className="mt-1.5 text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                      {userInfo?.username}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">角色</Label>
                    <div className="mt-1.5 text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                      {userInfo?.role}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">所属部门</Label>
                    <div className="mt-1.5 text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                      {userInfo?.department || '-'}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">创建时间</Label>
                    <div className="mt-1.5 text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                      {userInfo?.created_at ? new Date(userInfo.created_at).toLocaleString('zh-CN') : '-'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* 可编辑信息 */}
              <div>
                <h2 className="text-sm font-medium text-gray-500 mb-4">编辑信息</h2>
                <div className="grid grid-cols-4 gap-6">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">姓名 <span className="text-red-500">*</span></Label>
                    <Input
                      value={form.real_name}
                      onChange={(e) => setForm({ ...form, real_name: e.target.value })}
                      className="h-9"
                      placeholder="输入姓名"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">邮箱（用于接收流程通知）</Label>
                    <Input
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="h-9"
                      placeholder="输入邮箱"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">电话</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="h-9"
                      placeholder="输入电话号码"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">重置密码</Label>
                    <div className="relative">
                      <Input
                        type={passwordVisible ? 'text' : 'password'}
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="h-9 pr-9"
                        placeholder="留空则不修改"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                      >
                        {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* 保存按钮 */}
                <div className="flex justify-end mt-6">
                  <Button
                    onClick={handleSave}
                    disabled={submitting}
                    className="bg-blue-600 hover:bg-blue-700 px-8"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        保存中...
                      </>
                    ) : (
                      '保存'
                    )}
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* 消息通知设置 */}
              <div>
                <h2 className="text-sm font-medium text-gray-500 mb-4">消息通知</h2>
                <div className="text-xs text-gray-400 mb-4 space-y-1">
                  <p>
                    候选人流程变化（筛选通过/淘汰、面试通过/淘汰等）与新的待办任务，将按下方开关推送给您，
                    <span className="text-gray-500">两项默认均为关闭，需要接收通知请手动开启。</span>
                  </p>
                  <p>
                    · 邮件通知：填写<span className="text-gray-500">任意可用邮箱</span>即可开启；
                  </p>
                  <p>
                    · 飞书通知：需填写<span className="text-gray-500">公司邮箱</span>或
                    <span className="text-gray-500">飞书账号绑定的手机号</span>（二者其一即可），
                    开启时系统会到飞书通讯录校验，匹配不到将无法开启。
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-6 max-w-3xl">
                  <div className="flex items-center justify-between border border-gray-100 rounded-md px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-blue-500" />
                      <div>
                        <div className="text-sm text-gray-800">邮件通知</div>
                        <div className="text-xs text-gray-400">
                          {hasEmail ? `发送至：${userInfo?.email}` : '未填写邮箱，无法开启'}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={notifyEmail}
                      disabled={savingNotify || (!hasEmail && !notifyEmail)}
                      onChange={(v) => handleToggleNotify('email', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between border border-gray-100 rounded-md px-4 py-3">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-4 h-4 text-green-600" />
                      <div>
                        <div className="text-sm text-gray-800">飞书通知</div>
                        <div className="text-xs text-gray-400">
                          {hasContact
                            ? '开启时按邮箱/手机号匹配飞书账号，匹配失败将无法开启'
                            : '未填写邮箱和电话，无法开启'}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={notifyFeishu}
                      disabled={savingNotify || (!hasContact && !notifyFeishu)}
                      onChange={(v) => handleToggleNotify('feishu', v)}
                    />
                  </div>
                </div>
              </div>

              {isHRUser && <div className="border-t border-gray-100" />}

              {/* 简历抓取邮箱（仅HR可见） */}
              {isHRUser && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MailSearch className="w-4 h-4 text-amber-500" />
                    <h2 className="text-sm font-medium text-gray-500">简历抓取邮箱</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {fetchEnabled ? '已开启（自动扫描新邮件附件简历）' : '已关闭'}
                    </span>
                    <Switch
                      checked={fetchEnabled}
                      disabled={savingFetch}
                      onChange={(v) => handleSaveFetchConfig(v)}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  开启后系统会定时扫描该邮箱的新邮件（IMAP 协议收信），识别附件中的简历并存入「人才储备 - 待处理」，
                  开启前需填写下方三项配置。登录凭证只需一个：163/126/QQ 等邮箱填「授权码」，
                  企业邮箱填登录密码；服务器填收件服务器地址（如 imap.163.com，填 smtp.163.com 也会自动识别）。
                  凭证与服务器默认遮挡，点击眼睛图标查看真实值。
                  {userInfo?.resume_fetch_last_run_at && (
                    <span className="ml-1">
                      上次扫描：{new Date(userInfo.resume_fetch_last_run_at).toLocaleString('zh-CN')}
                    </span>
                  )}
                </p>
                <div className="grid grid-cols-3 gap-6 max-w-5xl">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">简历抓取邮箱</Label>
                    <Input
                      value={fetchForm.resume_fetch_email}
                      onChange={(e) => setFetchForm({ ...fetchForm, resume_fetch_email: e.target.value })}
                      className="h-9"
                      placeholder="可与接收通知邮箱相同"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">邮箱密码 / 授权码（二选一）</Label>
                    <div className="relative">
                      <Input
                        value={
                          fetchPasswordVisible
                            ? (revealed.password ?? fetchForm.resume_fetch_password)
                            : fetchForm.resume_fetch_password
                        }
                        onChange={(e) => {
                          setFetchForm({ ...fetchForm, resume_fetch_password: e.target.value });
                          if (fetchPasswordVisible) setRevealed({ ...revealed, password: e.target.value });
                        }}
                        className="h-9 pr-9"
                        placeholder="163等邮箱填授权码，企业邮箱填密码"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => handleReveal('password')}
                      >
                        {fetchPasswordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">IMAP 服务器（收件服务器）</Label>
                    <div className="relative">
                      <Input
                        type={fetchSmtpVisible ? 'text' : 'password'}
                        value={fetchForm.resume_fetch_smtp}
                        onChange={(e) => setFetchForm({ ...fetchForm, resume_fetch_smtp: e.target.value })}
                        className="h-9 pr-9"
                        placeholder="如：imap.163.com"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => handleReveal('smtp')}
                      >
                        {fetchSmtpVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button
                    onClick={() => handleSaveFetchConfig()}
                    disabled={savingFetch}
                    className="bg-blue-600 hover:bg-blue-700 px-8"
                  >
                    {savingFetch ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        保存中...
                      </>
                    ) : (
                      '保存抓取配置'
                    )}
                  </Button>
                </div>
              </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 确认弹窗 */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>确认修改</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">确定要保存个人信息的修改吗？</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              取消
            </Button>
            <Button onClick={handleConfirmSave} className="bg-blue-600 hover:bg-blue-700">
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
